// ** React Imports
import { Fragment, useState } from "react";

// ** MUI Components
import {
  Avatar,
  Stepper,
  StepLabel,
  IconButton,
  Button,
  Box,
  Typography,
  Card,
  CardContent,
  MenuItem,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  InputAdornment,
  Tooltip,
  Paper,  
  FormControlLabel,
  FormControl,
  FormLabel,
  Grid,
  Divider,
  ToggleButton,
  ToggleButtonGroup,
  Checkbox,
  Collapse,
} from "@mui/material";
import MuiStep from "@mui/material/Step";
import { styled, useTheme } from "@mui/material/styles";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";

// ** Icon Imports
import Icon from "../../../components/icon";
import AddIcon from "@mui/icons-material/Add";
import CloseIcon from "@mui/icons-material/Close";
import CorporateFareIcon from "@mui/icons-material/CorporateFare";
import useMediaQuery from "@mui/material/useMediaQuery";

// ** Custom Component Imports
import CustomTextField from "../../../components/mui/text-field";
import StepperCustomDot from "../../../components/Stepper/StepperCustomDot";
import CustomAvatar from "../../../components/mui/avatar";
import DateRangeCalendar from "../../Customers/component/DateRangeCalendar";
import CustomAutocomplete from "../../../components/mui/autocomplete";

// ** Styled Component
import StepperWrapper from "../../../components/styles/mui/stepper";

// ** Utils Import
import { hexToRGBA } from "../../../components/utils/hex-to-rgba";

// ** Third Party Imports
import Draggable from "react-draggable";
import dayjs from "dayjs";

// Import the success dialog
import AssessmentSuccessDialog from "./Assessmentsuccessdialog";
import { useCreateAssessmentLogic } from "../logic/create-assessment-logic";
import AddCustomerDialog from "screens/Customers/component/AddCustomer";
import AddContactDialog from "screens/Customers/component/AddContact";
import TagManagementDialog from "screens/Customers/component/TagManagement";
import AddBundleDialog from "./AddBundle";
import AddServiceDialog from "./AddService";
import PreAttachment from "screens/Requests/component/PreAttachment";
import AddBundle from "./AddBundle";
import AddService from "./AddService";
import AddJobsiteDialog from "screens/Customers/component/AddJobsite";

const steps = [
  {
    serial: "1",
    title: "Assessment Details",
    icon: "tabler:list-details",
  },
  // {
  //   serial: "2",
  //   title: "Assessment Form",
  //   icon: "tabler:align-box-left-stretch",
  // },
  {
    serial: "2",
    title: "Link Records",
    icon: "tabler:link",
  },
  {
    serial: "3",
    title: "Notes",
    icon: "tabler:ballpen",
  },
  {
    serial: "4",
    title: "Assign To",
    icon: "tabler:user-share",
  },
];

const Step = styled(MuiStep)(({ theme }) => ({
  "& .MuiStepLabel-root": {
    paddingTop: 0,
  },
  "& .MuiStepLabel-iconContainer": {
    display: "none",
  },
  "& .step-subtitle": {
    color: `${theme.palette.text.disabled} !important`,
  },
  "& + svg": {
    color: theme.palette.text.disabled,
  },
  "&.Mui-completed .step-title": {
    color: theme.palette.text.disabled,
  },
}));

const StepperHeaderContainer = styled(CardContent)(({ theme }) => ({
  [theme.breakpoints.down("md")]: {
    borderRight: 0,
    borderBottom: "none",
  },
}));

function DraggablePaper(props) {
  return (
    <Draggable
      handle="#draggable-dialog-title"
      cancel={'[class*="MuiDialogContent-root"]'}
    >
      <Paper {...props} />
    </Draggable>
  );
}

const CreateAssessment = ({ open, handleClose, setLoading }) => {
  const theme = useTheme();
  const [activeStep, setActiveStep] = useState(0);
  const isSmallScreen = useMediaQuery(theme.breakpoints.down('md'));
  
  // Add state for success dialog
  const [openSuccessDialog, setOpenSuccessDialog] = useState(false);
  const [newAssessmentID, setNewAssessmentID] = useState(null);

  const { CreateAssessmentProps } = useCreateAssessmentLogic({
    handleClose,
    activeStep,
    setActiveStep,
    steps,
    setLoading
  });

  const { 
    isServiceEmpty,
    setIsServiceEmpty,
    handleOpenServiceDlg,
    handleCloseServiceDlg,
    openServiceDlg,
    openAddBundle,
    setOpenAddBundle,
    isBundleEmpty,
    setIsBundleEmpty,
    handleOpenBundleDlg,
    handleCloseBundleDlg,
    handleSave,
    renderContent, 
    handleBack, 
    handleAssessmentNext, 
    allCustomers,
    allContacts,
    openNewCustomerDlg,
    setOpenNewCustomerDlg,
    fetchData,
    openNewContactDlg,
    handleCloseContactDialog,
    customer,
    fetchContacts,   
    toast, 
    openTagManagementDlg,
    handleCloseTagManagementDlg,              
    handleAddTag,
    handleDeleteTag,
    serviceRequest,
    Tag,
     openNewJobsiteDlg,
    setOpenNewJobsiteDlg,
    propertyArea,
    areaMeasurement,
    activeStepJobsite,
    jobsiteContent,
    clearPropertyArea,
    fetchPropertyArea,
    clearAreaMeasurement,
    setActiveStepJobsite,
    setPropertyArea,
    setAreaMeasurement,
    navigate,
    setAlertList,
    handleJobsiteChange,
    handleCloseNewJobsiteDlg,
    alertList,
    files,
    setFiles,
    jobsite,
    NProgress,
    fetchJobsites,
    propertyAreaList,
    setTag,
    resetRequest,  
    AddServiceProps,
    AddBundleProps,
    WorkRequirementsProps,
    AttachmentProps, 
    ResetWorkRequirement,
    // Make sure these functions are available from your logic
    validateRequestField,
    SaveService,
    ResetServicesAndBundlesCollapse,
    setSchedulingReq,
    scheduleStatus,
    fetchRequests
  } =  CreateAssessmentProps;

  const handleAssessmentClose = () => {
    handleClose(false);
    setActiveStep(0);
    resetRequest();
    ResetWorkRequirement();
  };

  // Add handler for when assessment is created
  const handleAssessmentCreated = (assessmentID) => {
    setNewAssessmentID(assessmentID);
    setOpenSuccessDialog(true);
  };

  // Add handler for success dialog close
const handleSuccessDialogClose = (action) => {
  setOpenSuccessDialog(false);
  if (action === "now") {
    // The schedule dialog will be opened by the AssessmentSuccessDialog itself
    // No need to close the creation dialog since it's already closed
  }
  // No need to handle "later" or "close" actions for the creation dialog
  // since it's already closed when we open the success dialog
};

  // Modify the handleAssessmentNext function
  // Modify the handleAssessmentNext function
const handleAssessmentNextModified = async () => {
  if(validateRequestField()){
    if (activeStep === 0) {
      SaveService();
      setActiveStep((prevActiveStep) => prevActiveStep + 1);
      ResetServicesAndBundlesCollapse();
    }
    
    if (activeStep === 1 || activeStep === 2 || activeStep === 3) {
      ResetServicesAndBundlesCollapse();
      setActiveStep((prevActiveStep) => prevActiveStep + 1);
    }

    if (activeStep === steps.length - 1) {
      const b = await SaveService();
      if(b){
        // Close the creation dialog first
        handleAssessmentClose();
        // Then open the success dialog
        handleAssessmentCreated(serviceRequest.requestID); // Pass the assessment ID
      } else {
        toast.error(`Assessment creation failed. Please try again.`, {
          position: "top-right",
          autoClose: 5000,
          hideProgressBar: false,
          closeOnClick: false,
          pauseOnHover: true,
          draggable: true,
          progress: undefined,
          theme: "light",
              style: {
                fontFamily: '"DM Sans", sans-serif',
                fontSize: '15px',
              },
        });  
      }
      setActiveStep(0);
    }
  }  
};
  return (
    <>
    <Dialog
      fullWidth={true}
      maxWidth="lg"
      open={open}
      onClose={() => handleAssessmentClose()}
      PaperComponent={isSmallScreen ? Paper : DraggablePaper}
      aria-labelledby="form-dialog-title"
       PaperProps={{
          sx: {
            height: "100%",
            maxHeight: "95vh",
            width: isSmallScreen ? "95vw" : "100%",
            m: isSmallScreen ? 2 : 0,
          },
        }}
        sx={{
          "& .MuiDialog-container": {
            alignItems: isSmallScreen ? "flex-end" : "center",
          },
        }}
    >
      <DialogTitle
        style={{ cursor: "move", borderBottom: "1px solid #D9D9D9" }}
        id="draggable-dialog-title"
      >
         <Box
                   sx={{
                     display: "flex",
                     justifyContent: "space-between",
                     alignItems: "center",
                     flexDirection: isSmallScreen ? "column" : "row",
                     gap: isSmallScreen ? 2 : 0,
                   }}
                 >
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 4,
              }}
            >
            <Typography variant="h3" fontWeight="bold">
              Create Assessment
            </Typography>            
          </Box>
          <StepperHeaderContainer>
            <StepperWrapper>
              <Stepper
                activeStep={activeStep}
                connector={<></>}
                sx={{ justifyContent: "space-between" ,  gap: isSmallScreen ? 2 : 0, }}
              >
                {steps.map((step, index) => {
                  const RenderAvatar =
                    activeStep >= index ? CustomAvatar : Avatar;
                  const isStepActive = activeStep === index;
                  return (
                    <Step key={index} sx={{ paddingBottom: 0 }}>
                      <StepLabel StepIconComponent={StepperCustomDot}>
                        <div className="step-label">
                          <RenderAvatar
                            variant="rounded"
                            {...(activeStep >= index && {
                              skin: "light",
                            })}
                            {...(isStepActive && { skin: "filled" })}
                            {...(activeStep >= index && {
                              color: "success",
                            })}
                            sx={{
                              ...(isStepActive && {
                                boxShadow: (theme) => theme.shadows[3],
                                color: "#fff", // white when active
                              }),
                              ...(activeStep > index && {
                                color: (theme) => theme.palette.mode === 'dark' ? 'rgba(208, 212, 241, 0.48)' : hexToRGBA(theme.palette.primary.main, 0.4),
                              }),
                            }}
                          >
                            <Icon icon={step.icon} />
                          </RenderAvatar>
                          <div>
                            <Typography
                              variant="subtitle2"
                              color="text.secondary"
                              {...(activeStep === index && {
                                fontWeight: "550",
                                color: "text.primary",
                              })}
                            >
                              {step.title}
                            </Typography>
                          </div>
                        </div>
                      </StepLabel>
                    </Step>
                  );
                })}
              </Stepper>
            </StepperWrapper>
          </StepperHeaderContainer>
        </Box>
      </DialogTitle>
      <DialogContent sx={{ position: 'relative'}}>
        <Box sx={{ height: '100%' }}>{renderContent()}</Box>
      </DialogContent>
      <DialogActions
       sx={{
                   display: "flex",
                   justifyContent: "space-between",
                   padding: isSmallScreen ? "16px !important" : "20px 24px !important",
                   boxShadow: `0px -4px 8px rgb(0, 0, 0, 0.1)`,
                   zIndex: 1,
                   flexDirection: isSmallScreen ? "column-reverse" : "row",
                   gap: isSmallScreen ? 2 : 0,
                 }}
        className="dialog-actions-dense"
      >
         <Box sx={{ width: isSmallScreen ? "100%" : "auto" }}>
          <Button
            variant="contained"
            color="info"
            onClick={() => handleAssessmentClose()}
             fullWidth={isSmallScreen}
          >
            Cancel
          </Button>
        </Box>
        <Box
                     sx={{
                       display: "flex",
                       width: isSmallScreen ? "100%" : "auto",
                       gap: isSmallScreen ? 2 : 4,
                       flexDirection: isSmallScreen ? "column" : "row",
                     }}
                   >
          {activeStep !== 0 && (
            <>
              <Button variant="tonal" color='info'>Skip</Button>
              <Button variant="tonal" color='info'>Save as Draft</Button>
            </>
          )}
          <Button
            variant="outlined"
            color="main"
            disabled={activeStep === 0}
            onClick={handleBack}
             fullWidth={isSmallScreen}
          >
            Back
          </Button>
          <Button
            variant="contained"
            color="main"
            onClick={handleAssessmentNextModified} // Use the modified function
             fullWidth={isSmallScreen}
          >
            {activeStep === steps.length - 1 ? "Save Assessment" : "Next Step"}
          </Button>
        </Box>
      </DialogActions>
    </Dialog>
    
    {/* Success Dialog */}
    <AssessmentSuccessDialog
      open={openSuccessDialog}
      onClose={handleSuccessDialogClose}
      requestID={newAssessmentID}
      setSchedulingReq={setSchedulingReq}
      setLoading={setLoading}
      scheduleStatus={scheduleStatus}
      fetchRequests={fetchRequests}
    />

    <AddCustomerDialog
        open={openNewCustomerDlg}
        setOpenNewCustomerDlg={setOpenNewCustomerDlg}
        fetchData={fetchData}
        allCustomers={allCustomers}
      />

         <AddJobsiteDialog
             open={openNewJobsiteDlg}
             handleClose={handleCloseNewJobsiteDlg}
             setJobsiteOpen={setOpenNewJobsiteDlg}
             customer={customer}
             fetchContactData={fetchContacts}
             toast={toast}
             jobsite={jobsite}
             handleJobsiteChange={handleJobsiteChange}
             navigate={navigate}
             NProgress={NProgress}
             fetchContacts={fetchContacts}
             fetchJobsites={fetchJobsites}
             contacts={allContacts}
             propertyArea={propertyArea}
             setPropertyArea={setPropertyArea}
             areaMeasurement={areaMeasurement}
             setAreaMeasurement={setAreaMeasurement}
             setLoading={setLoading}
             activeStep={activeStepJobsite}
             setActiveStep={setActiveStepJobsite}
             clearAreaMeasurement={clearAreaMeasurement}
             fetchPropertyArea={fetchPropertyArea}
             propertyAreaList={propertyAreaList}
             clearPropertyArea={clearPropertyArea}
             jobsiteContent={jobsiteContent}
             alertList={alertList}
             setAlertList={setAlertList}
             files={files}
             setFiles={setFiles}
           />
      <AddContactDialog
        open={openNewContactDlg}
        handleClose={handleCloseContactDialog}
        customer = {customer}
        setLoading={setLoading}
        fetchContactData = {fetchContacts}   
        toast = {toast}                 
        contacts = {allContacts}
      />

      <TagManagementDialog
        open = {openTagManagementDlg}
        handleClose = {handleCloseTagManagementDlg}      
        data = {{title:'Tag Management'}}
        handleAddTag = {handleAddTag}
        handleDeleteTag = {handleDeleteTag}
        tags = {serviceRequest?.tags}
        tag = {Tag}
        setTag = {setTag}
      />

      <AddBundle
        {...AddBundleProps}
      />

      <AddService
        {...AddServiceProps}
      />

      <PreAttachment
        Title="Upload Attachment"
        {...AttachmentProps}
      />
    </>    
  );
};

export default CreateAssessment;